/**
 * What a live session's turns carry since D372, drawn as the workbench draws them: an agent's question to a named
 * person with its answer in place (the person asked answers here, in one press or in their own words), a finished
 * turn's changed files under its reply, a divider (handed over, forked, went back), the usage limit's banner above the
 * composer, and what an approval would do (the command it runs, the file and the change it makes).
 *
 * Ported from the workbench at 9a76e5e2 (next/0.89.21): AskCard (webview-ui/src/components/facilitator/
 * TeamWorkPeople.tsx; the answer goes as `sessions.send` with `intent: "answer"` rather than a Team Work post),
 * TurnChanges (components/facilitator/TurnChanges.tsx, over the stream's `changes`: since D414 a turn that names its
 * ending checkpoint opens each file's diff in place, `git.diff { rootId, checkpoint, path }`, for the person who
 * controls the session; before it the files are listed only), ConversationMarkerLine (components/facilitator/MessageActions.tsx) and
 * LimitBanner (components/agents/LimitBanner.tsx, with HandOffMenu: "Hand off to…" offers the same runtime's other
 * signed-in accounts and the other runtimes the machine reported, as `sessions.handOff`; the backend's own words are the
 * limit event's `detail` since D414, the stopped status's that came with the limit before it). The diff is drawn by the console's ReviewPatch, itself the workbench's. Words are `@aioe/shared`'s.
 */

import type { GitDiffAnswer, HandOffOption, LiveApprovalDetail, LiveChangesItem, LiveMarker, LiveMessageItem } from '@aioe/shared';
import { turnDiffEmptyWords, unifiedPatchOf } from '@aioe/shared';
import { changedFileTree, handedOffWords, limitBannerLines, limitRanWords, markerWords, numberUnifiedDiff, turnChangesSummary } from '@aioe/shared';
import { ChevronDown, ChevronRight, Clock, Person } from '@gravity-ui/icons';
import { Description, Dropdown, Label, TextArea } from '@heroui/react';
import { useMemo, useState } from 'react';

import { useNow } from '../../hooks.ts';
import { ReviewPatch } from '../review/ReviewPatch.tsx';
import '../review/reviewPatch.css';
import { Button } from '../ui.tsx';
import type { LiveLimit } from './liveModel.ts';
import { liveProblem } from './useLiveSession.ts';

const NOOP = () => undefined;

// ── A question asked of a named person ─────────────────────────────────

/**
 * An agent's question to a named person, under its message: who it is asked of and the answer once there is one. The
 * person asked answers here; someone covering them may too, from "Answer for …" (the workbench refuses anyone else,
 * in its own words). `canAnswer`: this reader controls the session and the machine is reachable.
 */
export function AskCard({ message, me, answer, canAnswer, onAnswer }: {
  message: LiveMessageItem;
  /** The signed-in person's id. */
  me?: string;
  /** The answer, when the session has one. */
  answer?: LiveMessageItem;
  canAnswer: boolean;
  onAnswer: (questionId: string, text: string) => Promise<string | undefined>;
}) {
  const ask = message.asks!;
  const mine = Boolean(me) && ask.person.id === me;
  const toName = ask.person.name?.trim() || 'someone';
  const [covering, setCovering] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState<string | null>(null);
  const [said, setSaid] = useState<{ ok: boolean; message?: string } | null>(null);
  const send = async (value: string) => {
    if (!value.trim()) return;
    setSending(value);
    const problem = await onAnswer(ask.questionId, value.trim());
    setSending(null);
    setSaid(problem ? { ok: false, message: problem } : { ok: true });
    if (!problem) setText('');
  };
  const open = canAnswer && (mine || covering) && !answer && !said?.ok;
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-separator bg-surface px-3 py-2.5" data-ask-card={answer ? 'answered' : mine ? 'mine' : 'waiting'}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
        <Person className="size-3.5 text-accent" aria-hidden />
        <span>
          Asked of <span className="font-medium text-foreground">{mine ? 'you' : toName}</span>
        </span>
      </div>
      {/* The message already says the question, as on the desktop; the card says it only when the message does not. */}
      {ask.question.trim() && !message.text.includes(ask.question.trim()) ? <p className="m-0 text-sm text-foreground">{ask.question}</p> : null}
      {answer ? (
        <p className="m-0 text-sm text-foreground" data-ask-answer>
          <span className="font-medium">{answer.author?.id && answer.author.id === me ? 'You' : (answer.author?.name ?? 'The answer')}</span>: “{answer.text}”
        </p>
      ) : open ? (
        ask.choices?.length ? (
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Answer">
            {ask.choices.map((o) => (
              <Button key={o} size="sm" variant="secondary" isPending={sending === o} isDisabled={!!sending && sending !== o} onPress={() => void send(o)}>
                {o}
              </Button>
            ))}
          </div>
        ) : (
          <div className="flex items-end gap-2">
            <TextArea aria-label="Your answer" rows={2} className="min-w-0 flex-1 text-sm" placeholder="Your answer" value={text} onChange={(e) => setText(e.target.value)} />
            <Button size="sm" isPending={!!sending} isDisabled={!text.trim()} onPress={() => void send(text)}>
              Answer
            </Button>
          </div>
        )
      ) : !said ? (
        <p className="m-0 flex flex-wrap items-center gap-x-2 text-xs text-muted">
          <span>{mine ? 'Waiting for your answer.' : `Waiting for ${toName}, or whoever covers them.`}</span>
          {canAnswer && !mine ? (
            <button type="button" className="cursor-[var(--cursor-interactive)] border-0 bg-transparent p-0 text-xs text-accent hover:underline" onClick={() => setCovering(true)}>
              Answer for {toName}
            </button>
          ) : null}
        </p>
      ) : null}
      {said ? (
        <p className={`m-0 text-xs ${said.ok ? 'text-success' : 'text-danger'}`} role="status">
          {said.ok ? 'Sent. The agent has your answer.' : said.message}
        </p>
      ) : null}
    </div>
  );
}

// ── A finished turn's changes ──────────────────────────────────────────

/** Reads one file of a finished turn as that turn changed it (D414 `git.diff`). */
export type OpenTurnDiff = (item: { rootId: string; checkpoint: string }, path: string) => Promise<GitDiffAnswer>;

type DiffRead = { state: 'reading' } | { state: 'read'; answer: GitDiffAnswer } | { state: 'failed'; problem: string };

/** One file's change at a turn, drawn read-only as the console draws a commit's; read once, when first opened. */
function TurnFileDiff({ read, path }: { read: DiffRead | undefined; path: string }) {
  const file = read?.state === 'read' ? (read.answer.files.find((f) => f.path === path) ?? read.answer.files[0]) : undefined;
  const rows = useMemo(() => (file?.hunks.length ? numberUnifiedDiff(unifiedPatchOf([file])) : []), [file]);
  if (!read || read.state === 'reading') return <p className="m-0 py-1 text-xs text-muted">Reading the change…</p>;
  if (read.state === 'failed') return <p className="m-0 py-1 text-xs text-danger" role="alert">{read.problem}</p>;
  const empty = turnDiffEmptyWords(file, read.answer.note);
  return (
    <div className="flex min-w-0 flex-col gap-1 py-1" data-turn-file-diff={path}>
      {empty ? (
        <p className="m-0 text-xs text-muted">{empty}</p>
      ) : (
        <div className="diff-box max-h-96 overflow-y-auto" data-readonly>
          <ReviewPatch rows={rows} defaultPath={path} hideHeaders notes={[]} onAdd={NOOP} onEdit={NOOP} onRemove={NOOP} onReopen={NOOP} onResolve={NOOP} onSendBatch={NOOP} />
        </div>
      )}
      {!empty && file?.truncated ? <p className="m-0 text-xs text-muted">{file.truncated.reason}</p> : null}
    </div>
  );
}

/**
 * "N changed files" with the lines added and removed, and a compact tree by folder; three folders until opened. When the
 * turn names its ending checkpoint and the reader may read it (`onOpenDiff`), each file opens its change in place.
 */
export function ReplyChanges({ item, onOpenDiff }: { item: LiveChangesItem; onOpenDiff?: OpenTurnDiff }) {
  const [open, setOpen] = useState(false);
  const [shownFile, setShownFile] = useState<string>();
  const [reads, setReads] = useState<Record<string, DiffRead>>({});
  const at = item.checkpoint && item.rootId ? { rootId: item.rootId, checkpoint: item.checkpoint } : undefined;
  const canOpen = Boolean(at && onOpenDiff);
  const toggle = (path: string) => {
    if (!at || !onOpenDiff) return;
    setShownFile((p) => (p === path ? undefined : path));
    if (reads[path]?.state === 'read' || reads[path]?.state === 'reading') return;
    setReads((r) => ({ ...r, [path]: { state: 'reading' } }));
    onOpenDiff(at, path).then(
      (answer) => setReads((r) => ({ ...r, [path]: { state: 'read', answer } })),
      (e: unknown) => setReads((r) => ({ ...r, [path]: { state: 'failed', problem: liveProblem(e) } })),
    );
  };
  const summary = turnChangesSummary(item.files);
  const tree = changedFileTree(item.files);
  const shown = open ? tree : tree.slice(0, 3);
  const hiddenGroups = tree.length - shown.length;
  return (
    <section className="max-w-full rounded-xl border border-separator bg-surface/50 px-3 py-2 text-xs" aria-label={summary.label} data-turn-changes>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-semibold text-foreground">{summary.label}</span>
        <span className="tabular-nums text-success">+{summary.insertions}</span>
        <span className="tabular-nums text-danger">−{summary.deletions}</span>
      </div>
      <ul className="m-0 mt-1 flex list-none flex-col gap-1 p-0">
        {shown.map((group) => (
          <li key={group.dir || '.'}>
            {group.dir ? <span className="font-mono text-xs text-muted [overflow-wrap:anywhere]">{group.dir}/</span> : null}
            <ul className={`m-0 flex list-none flex-col p-0 ${group.dir ? 'pl-3' : ''}`}>
              {group.files.map((f) =>
                canOpen ? (
                  <li key={f.path} className="flex min-w-0 flex-col">
                    <button
                      type="button"
                      className="flex min-w-0 cursor-[var(--cursor-interactive)] items-center gap-1.5 rounded-md border-0 bg-transparent p-0 py-0.5 text-left text-xs hover:bg-default/40"
                      aria-expanded={shownFile === f.path}
                      aria-label={`${shownFile === f.path ? 'Hide' : 'Show'} the change to ${f.path}`}
                      data-open-diff={f.path}
                      onClick={() => toggle(f.path)}
                    >
                      {shownFile === f.path ? <ChevronDown className="size-3 shrink-0 text-muted" aria-hidden /> : <ChevronRight className="size-3 shrink-0 text-muted" aria-hidden />}
                      <span className="min-w-0 truncate font-mono text-xs text-foreground" title={f.path}>
                        {f.name}
                      </span>
                      <span className="ml-auto shrink-0 tabular-nums text-muted">
                        +{f.added} −{f.removed}
                      </span>
                    </button>
                    {shownFile === f.path ? <TurnFileDiff read={reads[f.path]} path={f.path} /> : null}
                  </li>
                ) : (
                  <li key={f.path} className="flex min-w-0 items-center gap-2">
                    <span className="min-w-0 truncate font-mono text-xs text-foreground" title={f.path}>
                      {f.name}
                    </span>
                    <span className="ml-auto shrink-0 tabular-nums text-muted">
                      +{f.added} −{f.removed}
                    </span>
                  </li>
                ),
              )}
            </ul>
          </li>
        ))}
      </ul>
      {hiddenGroups > 0 ? (
        <button type="button" className="mt-1 cursor-[var(--cursor-interactive)] border-0 bg-transparent p-0 text-xs text-muted hover:text-foreground" onClick={() => setOpen(true)}>
          {hiddenGroups} more folder{hiddenGroups === 1 ? '' : 's'}
        </button>
      ) : null}
    </section>
  );
}

// ── Dividers and the workbench's own lines ─────────────────────────────

/** A divider in the conversation: handed over, forked from, went back. */
export function MarkerLine({ marker }: { marker: LiveMarker }) {
  const words = markerWords(marker);
  return (
    <div className="flex items-center gap-3 text-xs text-muted" role="separator" aria-label={words.text} data-conversation-marker={marker.kind}>
      <span className="h-px flex-1 bg-separator" />
      <span className="flex max-w-[80%] flex-col items-center text-center">
        <span className="font-medium text-foreground/80">{words.text}</span>
        {words.detail ? <span>{words.detail}</span> : null}
      </span>
      <span className="h-px flex-1 bg-separator" />
    </div>
  );
}

/** A line the workbench itself wrote in the conversation (a member joined, a run ended): quiet, between the messages. */
export function SystemLine({ text }: { text: string }) {
  return (
    <p className="m-0 px-6 text-center text-xs text-muted [overflow-wrap:anywhere]" data-system-line>
      {text}
    </p>
  );
}

// ── The usage limit ─────────────────────────────────────────────────────

/**
 * The agent reached its usage limit: a warning (never an error), a countdown that ticks each minute, "Hand off to…" so the
 * work need not wait (the person it runs for, when the machine reported a runtime to go to), and what the backend said,
 * on request. `onHandOff` resolves with the workbench's sentence when it refused.
 */
export function LimitBanner({ limit, agent, options = [], onHandOff }: { limit: LiveLimit; agent?: string; options?: readonly HandOffOption[]; onHandOff?: (o: HandOffOption) => Promise<string | undefined> }) {
  const now = useNow();
  const lines = limitBannerLines(limit, now, agent);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ tone: 'ok' | 'problem'; text: string }>();
  const pick = async (o: HandOffOption) => {
    if (!onHandOff) return;
    setBusy(true);
    setNote(undefined);
    const problem = await onHandOff(o);
    setBusy(false);
    setNote(problem ? { tone: 'problem', text: problem } : { tone: 'ok', text: handedOffWords(o) });
  };
  const offer = Boolean(onHandOff) && options.length > 0 && !limit.handOff;
  const ran = limitRanWords(limit);
  return (
    <div data-limit-wait className="flex flex-col gap-1 rounded-xl border border-warning/40 bg-warning/10 px-3 py-2 text-xs leading-snug" role="status">
      <span className="flex items-center gap-1.5 font-medium text-warning">
        <Clock className="size-3.5 shrink-0" aria-hidden />
        {lines.summary}
      </span>
      <span className="text-foreground">{lines.action}</span>
      {ran ? (
        <span className="text-muted" data-limit-ran>
          {ran}
        </span>
      ) : null}
      {lines.lifts || offer ? (
        <span className="flex flex-wrap items-center gap-2">
          {lines.lifts ? (
            <span className="text-muted" data-limit-lifts>
              {lines.lifts}
            </span>
          ) : null}
          <span className="flex-1" />
          {offer ? (
            <Dropdown>
              <Button size="sm" variant="secondary" isPending={busy} isDisabled={busy} data-hand-off>
                Hand off to…
              </Button>
              <Dropdown.Popover placement="top end" className="w-72 max-w-[calc(100vw-2rem)]">
                <Dropdown.Menu aria-label="Hand off to" onAction={(k) => void pick(options.find((o) => o.id === k)!)}>
                  {options.map((o) => (
                    <Dropdown.Item key={o.id} id={o.id} textValue={o.label}>
                      <Label>{o.label}</Label>
                      <Description>{o.detail}</Description>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown>
          ) : null}
        </span>
      ) : null}
      {note ? (
        <span className={note.tone === 'problem' ? 'text-danger' : 'text-muted'} role={note.tone === 'problem' ? 'alert' : undefined}>
          {note.text}
        </span>
      ) : null}
      {limit.said ? (
        <details>
          <summary className="cursor-[var(--cursor-interactive)] text-muted">What the backend said</summary>
          <pre className="m-0 mt-1 max-h-24 overflow-auto whitespace-pre-wrap break-words font-mono text-xs text-muted" data-backend-said>
            {limit.said}
          </pre>
        </details>
      ) : null}
    </div>
  );
}

// ── What an approval would do ───────────────────────────────────────────

/** The workbench joins one file's diff after another with no `diff --git` line between: give each file one, so each is drawn under its name. */
export function withFileHeaders(diff: string): string {
  const lines = diff.split('\n');
  const out: string[] = [];
  lines.forEach((l, i) => {
    const next = lines[i + 1];
    const m = /^--- a\/(.+)$/.exec(l);
    if (m && next?.startsWith('+++ b/')) out.push(`diff --git a/${m[1]} b/${next.slice(6)}`);
    out.push(l);
  });
  return out.join('\n');
}

/** A unified diff, read-only, as the console draws a commit's. */
function ApprovalDiff({ diff, path }: { diff: string; path?: string }) {
  const rows = useMemo(() => numberUnifiedDiff(withFileHeaders(diff)), [diff]);
  const files = rows.filter((r) => r.kind === 'meta' && r.text.startsWith('diff --git ')).length;
  return (
    <div className="diff-box max-h-72 overflow-y-auto" data-readonly>
      <ReviewPatch rows={rows} defaultPath={path} hideHeaders={files <= 1} notes={[]} onAdd={NOOP} onEdit={NOOP} onRemove={NOOP} onReopen={NOOP} onResolve={NOOP} onSendBatch={NOOP} />
    </div>
  );
}

/**
 * The command a request would run, or the file it would touch and the change it would make. A change opens on its
 * own when it is short; a long one is a line that opens to it.
 */
export function ApprovalDetail({ detail }: { detail: LiveApprovalDetail }) {
  const lines = detail.diff ? detail.diff.split('\n').length : 0;
  const [open, setOpen] = useState(lines > 0 && lines <= 24);
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div className="flex min-w-0 flex-col gap-1" data-approval-detail>
      {detail.command ? (
        <pre className="m-0 max-h-32 overflow-auto whitespace-pre-wrap rounded-lg bg-surface-secondary px-2 py-1.5 font-mono text-xs leading-4 text-foreground [overflow-wrap:anywhere]" aria-label="Command">
          {detail.command}
        </pre>
      ) : null}
      {detail.diff ? (
        <>
          <button type="button" className="flex min-w-0 cursor-[var(--cursor-interactive)] items-center gap-1.5 border-0 bg-transparent p-0 text-left text-xs text-muted hover:text-foreground" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
            <Chevron className="size-3 shrink-0" aria-hidden />
            <span className="min-w-0 truncate">
              {open ? 'Hide the change' : 'Show the change'}
              {detail.path ? <span className="font-mono"> · {detail.path}</span> : null}
            </span>
          </button>
          {open ? <ApprovalDiff diff={detail.diff} path={detail.path} /> : null}
        </>
      ) : detail.path ? (
        <span className="min-w-0 truncate font-mono text-xs text-muted" title={detail.path}>
          {detail.path}
        </span>
      ) : null}
    </div>
  );
}
